import { ArrowUpRight, ArrowDownRight, Minus } from "@phosphor-icons/react";
import { formatValue } from "./perfDemoData";

export function PerfBanner({ children }) {
  return (
    <div className="border border-amber-300 bg-amber-50 px-4 py-2.5 mb-6 text-xs sm:text-sm text-amber-900 flex items-start gap-2" data-testid="perf-mockup-banner">
      <span className="font-semibold uppercase tracking-wide shrink-0">Aperçu</span>
      <span>{children}</span>
    </div>
  );
}

export function PeriodSwitch({ value, onChange }) {
  const opts = [
    { id: "day", label: "Jour" },
    { id: "week", label: "Semaine" },
    { id: "month", label: "Mois" },
  ];
  return (
    <div className="inline-flex border border-zinc-300 bg-white" data-testid="perf-period-switch">
      {opts.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          data-testid={`perf-period-${o.id}`}
          className={`px-4 h-9 text-xs sm:text-sm transition-colors ${
            value === o.id ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function KpiCard({ metric, value, prev, target, testId }) {
  const pct = target ? Math.round((value / target) * 100) : null;
  const good = metric.dir === "down" ? pct !== null && pct <= 100 : pct !== null && pct >= 100;
  const delta = prev != null && value != null && prev !== 0 ? Math.round(((value - prev) / prev) * 1000) / 10 : null;
  const deltaGood = delta == null ? null : metric.dir === "down" ? delta < 0 : delta > 0;

  return (
    <div className="bg-white border border-zinc-200 p-4 sm:p-5 flex flex-col justify-between min-h-[148px]" data-testid={testId}>
      <div>
        <div className="flex items-start justify-between gap-2">
          <div className="label-tiny text-zinc-400 leading-snug">{metric.name}</div>
          <SourceTag source={metric.source} />
        </div>
        <div className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mt-2 tabular-nums">
          {formatValue(value, metric.unit)}
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {target != null && (
          <>
            <div className="flex items-center justify-between text-[11px] text-zinc-500">
              <span>Objectif {formatValue(target, metric.unit)}</span>
              <span className={`font-semibold tabular-nums ${good ? "text-green-700" : "text-red-600"}`}>{pct} %</span>
            </div>
            <div className="h-1.5 bg-zinc-100">
              <div
                className={`h-full ${good ? "bg-green-600" : "bg-red-500"}`}
                style={{ width: `${Math.min(pct ?? 0, 100)}%` }}
              />
            </div>
          </>
        )}
        {delta != null && (
          <div className={`flex items-center gap-1 text-[11px] ${deltaGood ? "text-green-700" : "text-red-600"}`}>
            {delta === 0 ? <Minus size={12} /> : deltaGood ? <ArrowUpRight size={12} weight="bold" /> : <ArrowDownRight size={12} weight="bold" />}
            <span className="tabular-nums font-medium">{delta > 0 ? "+" : ""}{delta} %</span>
            <span className="text-zinc-400">vs période précédente</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function SourceTag({ source }) {
  const map = {
    MANUAL: "bg-zinc-100 text-zinc-600",
    CALCULATED: "bg-blue-50 text-blue-700",
    API: "bg-violet-50 text-violet-700",
  };
  const label = { MANUAL: "Saisie", CALCULATED: "Calculé", API: "API" }[source];
  return <span className={`text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 ${map[source]}`}>{label}</span>;
}

export function SectionTitle({ children, right }) {
  return (
    <div className="flex items-center justify-between mb-3 mt-8 first:mt-0">
      <h2 className="font-heading text-lg font-semibold tracking-tight">{children}</h2>
      {right}
    </div>
  );
}
